import { useMemo } from "react";
import { Link } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import type { Edge } from "@xyflow/react";
import { NODE_LABELS } from "../../lib/operationPorts.js";
import { TanTableViz } from "../TanTableViz.js";
import type { QueryColumnMeta, QueryResultsPayload } from "../QueryResultsGrid.js";
import { queryResultsToTanTableModel } from "../../lib/queryResultsToTanTable.js";
import { previewHeaderLabel } from "../../lib/previewHeader.js";
import { ColumnDomainSelect } from "../ColumnDomainControl.js";
import type { OperationCanvasNodeData, OperationCanvasNodeType } from "../OperationCanvasNode.js";
import { NodeMetadataFields, NodeOutputStats, NodeRunHistory, type NodeMetadata, type NodeOutputRecord, type NodeRunHistoryEntry } from "./nodeInspectorWidgets.js";
import type { ToastKind } from "../Toast.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import type { InspectorSlot } from "../inspector/inspectorModel.js";

const sqlExtensions = [sql({ dialect: PostgreSQL })];

export type ProviderInspectorTab =
  | "metadata"
  | "scope"
  | "sql"
  | "parameters"
  | "bindings"
  | "schema"
  | "cache"
  | "history";

// ADR-0129 — the provider inspector's 8 internal sections map onto the shared
// five-slot spine. `providerInspectorTab` stays the single source of truth (the
// editor's cache-load effect keys off `=== "cache"`); the outer spine slot is
// derived from it and drives InspectorTabs in controlled mode. Multi-section
// slots (Setup, Data) render their sections under an inner sub-tab bar.
const PROVIDER_SLOT_OF: Record<ProviderInspectorTab, InspectorSlot> = {
  metadata: "info",
  scope: "setup",
  sql: "setup",
  parameters: "setup",
  bindings: "setup",
  schema: "data",
  cache: "data",
  history: "runs",
};

/** The sub-section a spine slot lands on when selected from the spine tab bar. */
function providerDefaultTabForSlot(slot: InspectorSlot): ProviderInspectorTab {
  switch (slot) {
    case "setup":
      return "scope";
    case "data":
      return "schema";
    case "runs":
      return "history";
    case "info":
    default:
      return "metadata";
  }
}

type ProviderRef = { type: "shared"; provider_id: string } | { type: "local"; local_provider_id: string };

type BindingSource =
  | { type: "default" }
  | { type: "const"; value: string | number | boolean | null }
  | { type: "window"; window: string; field: "start_at" | "end_at" | "start_date" | "end_date" }
  | { type: "scope"; column: string }
  | { type: "variable"; variable: string };

type ProviderParamType = "string" | "int" | "decimal" | "bool" | "date" | "datetime";

type ProviderParameter = {
  name: string;
  type: ProviderParamType;
  required: boolean;
  default?: string | number | boolean | null;
  description?: string | null;
};

export type ProviderInspectorDraft = {
  name: string;
  description?: string | null;
  connector_id: string;
  source_spec: { specVersion: "source.v1"; kind: "sql"; sql: string };
  parameters: ProviderParameter[];
  schema_contract: { columns: QueryColumnMeta[] } | null;
  source_sql_locked?: boolean;
  /** ADR-0146: the provider's schema lock — managed on the provider, shown
   *  here read-only so a builder knows the shape is frozen. */
  schema_locked?: boolean;
  schema_locked_at?: string | null;
};

type ProviderPreviewState = {
  loading: boolean;
  error: string | null;
  result: QueryResultsPayload | null;
};

type CanvasEdge = Edge<{ edge_type: "data" | "scope" }>;

export type OperationInspectorProviderScopeProps = {
  selectedNode: OperationCanvasNodeType;
  operationMeta: NodeMetadata;
  providerInspectorTab: ProviderInspectorTab;
  setProviderInspectorTab: (tab: ProviderInspectorTab) => void;
  selectedProviderRef: ProviderRef | null;
  onProviderSourceKindChange: (kind: "local" | "shared") => void;
  onSharedProviderIdChange: (providerId: string) => void;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  providersForConnector: Array<{ id: string; name: string }>;
  edges: CanvasEdge[];
  nodes: OperationCanvasNodeType[];
  nodeOutputs: Record<string, NodeOutputRecord>;
  selectedProviderDraft: ProviderInspectorDraft | null;
  updateMaterialProviderDraft: (
    nodeId: string,
    updater: (draft: ProviderInspectorDraft) => ProviderInspectorDraft
  ) => void | Promise<void>;
  onPromoteLocalProvider: () => void | Promise<void>;
  onDetachSharedProvider: () => void | Promise<void>;
  selectedProviderPreview: ProviderPreviewState | null | undefined;
  executeSelectedProviderSql: () => void | Promise<void>;
  /** ADR-0147 — Domain override control on the Schema tab (local providers only;
   *  shared-bound nodes link out to the provider's Schema Contract instead). */
  columnOverrides?: Record<string, string>;
  onColumnDomainChange?: (columnName: string, domain: import("../ColumnDomainControl.js").ColumnDomain | null) => void;
  retypeBusy?: boolean;
  /** ADR-0128 — true while this node has an active async run (queued/running). */
  nodeRunActive: boolean;
  onStopExecution: () => void | Promise<void>;
  openNewParameterModal: () => void;
  openEditParameterModal: (idx: number) => void;
  deleteParameterFromList: (idx: number) => void | Promise<void>;
  bindingDescription: (paramName: string, current: BindingSource) => string;
  openAddBindingModal: () => void;
  openBindingModal: (param: ProviderParameter) => void;
  clearBinding: (paramName: string) => void | Promise<void>;
  connectors: Array<{ id: string; name: string }>;
  nodeCacheRefreshBusy: boolean;
  operationStatus: "draft" | "invalid" | "published" | "paused" | "validate";
  refreshProviderCache: (nodeId: string) => void | Promise<void>;
  nodeCacheLoading: Record<string, boolean>;
  nodeCache: Record<string, QueryResultsPayload | null>;
  nodeRunHistory: Record<string, NodeRunHistoryEntry[]>;
  nodeRunHistoryLoading: Record<string, boolean>;
  loadNodeRunHistory: (nodeId: string) => void;
  onSelectHistoryRun: (run: NodeRunHistoryEntry) => void;
  /** ADR-0128 — open a run's full-chain Trace from a History row. */
  onOpenTrace?: (runId: string) => void;
  pushToast: (t: { kind: ToastKind; message: string }) => void;
  /** ADR-0117: operation variable names, for the scope-list collision warning. */
  operationVariableNames: string[];
};

export function OperationInspectorProviderScope(props: OperationInspectorProviderScopeProps) {
  const {
    selectedNode,
    operationMeta,
    providerInspectorTab,
    setProviderInspectorTab,
    selectedProviderRef,
    onProviderSourceKindChange,
    onSharedProviderIdChange,
    setNodeData,
    providersForConnector,
    edges,
    nodes,
    nodeOutputs,
    selectedProviderDraft,
    updateMaterialProviderDraft,
    onPromoteLocalProvider,
    onDetachSharedProvider,
    selectedProviderPreview,
    executeSelectedProviderSql,
    columnOverrides,
    onColumnDomainChange,
    retypeBusy,
    nodeRunActive,
    onStopExecution,
    openNewParameterModal,
    openEditParameterModal,
    deleteParameterFromList,
    bindingDescription,
    openAddBindingModal,
    openBindingModal,
    clearBinding,
    connectors,
    nodeCacheRefreshBusy,
    operationStatus,
    refreshProviderCache,
    nodeCacheLoading,
    nodeCache,
    nodeRunHistory,
    nodeRunHistoryLoading,
    loadNodeRunHistory,
    onSelectHistoryRun,
    onOpenTrace,
    pushToast,
    operationVariableNames
  } = props;

  const sqlEditorLocked =
    selectedProviderRef?.type === "shared" || Boolean(selectedProviderDraft?.source_sql_locked);

  async function copyProviderSql() {
    const text = selectedProviderDraft?.source_spec.sql ?? "";
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      pushToast({ kind: "success", message: "SQL copied" });
    } catch {
      pushToast({ kind: "error", message: "Could not copy to clipboard" });
    }
  }

  const providerPreviewTanModel = useMemo(
    () => (selectedProviderPreview?.result ? queryResultsToTanTableModel(selectedProviderPreview.result) : null),
    [selectedProviderPreview?.result]
  );

  const nodeCacheTanModel = useMemo(() => {
    const cached = nodeCache[selectedNode.id];
    return cached ? queryResultsToTanTableModel(cached) : null;
  }, [nodeCache, selectedNode.id]);

  const isProviderNode = selectedNode.data.nodeType === "provider";

  // Setup sub-tabs (Scope is provider-only); Data sub-tabs.
  const setupSubTabs: Array<{ tab: ProviderInspectorTab; label: string }> = [
    ...(isProviderNode ? [{ tab: "scope" as ProviderInspectorTab, label: "Scope" }] : []),
    { tab: "sql", label: "SQL" },
    { tab: "parameters", label: "Parameters" },
    { tab: "bindings", label: "Bindings" }
  ];
  const dataSubTabs: Array<{ tab: ProviderInspectorTab; label: string }> = [
    { tab: "schema", label: "Schema" },
    { tab: "cache", label: "Cache" }
  ];

  const draftMissingFallback = (
    <div className="muted">
      {selectedProviderRef?.type === "shared" && selectedProviderRef.provider_id
        ? "Loading provider…"
        : "Select or create a provider definition first."}
    </div>
  );

  const renderSubTabBar = (
    label: string,
    items: Array<{ tab: ProviderInspectorTab; label: string }>
  ) => (
    <div className="reportTabs" role="tablist" aria-label={label}>
      {items.map((st) => (
        <button
          key={st.tab}
          className={`reportTabBtn${providerInspectorTab === st.tab ? " reportTabBtnActive" : ""}`}
          type="button"
          onClick={() => setProviderInspectorTab(st.tab)}
        >
          {st.label}
        </button>
      ))}
    </div>
  );

  // ── Slot content ──────────────────────────────────────────────────────────

  const infoContent = (
    <>
      <div className="field">
        <label className="label">Node label</label>
        <input
          className="input"
          value={selectedNode.data.label}
          onChange={(e) => setNodeData(selectedNode.id, (data) => ({ ...data, label: e.target.value }))}
        />
      </div>
      <div className="field">
        <label className="label">Provider source</label>
        <select
          className="input"
          value={selectedProviderRef?.type ?? "local"}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "local") onProviderSourceKindChange("local");
            else onProviderSourceKindChange("shared");
          }}
        >
          <option value="local">Local to operation</option>
          <option value="shared">Select provider</option>
        </select>
      </div>
      {selectedProviderRef?.type === "shared" ? (
        <>
          <div className="field">
            <label className="label">Shared provider</label>
            <select
              className="input"
              value={selectedProviderRef.provider_id}
              onChange={(e) => onSharedProviderIdChange(e.target.value)}
            >
              <option value="">— Select a provider —</option>
              {providersForConnector.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>
          {selectedProviderDraft ? (
            <>
              <div className="muted" style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", fontSize: 13 }}>
                <span style={{ opacity: 0.6 }}>🔒</span>
                <span>Shared provider — SQL and parameters are read-only. Convert to a local copy to edit.</span>
              </div>
              {selectedProviderDraft.source_sql_locked ? (
                <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
                  This library provider has production SQL lock enabled.
                </div>
              ) : null}
              {/* ADR-0146 §3.2 — one path per action: the lock is managed on the
                  provider, which is what makes it cover every consuming operation. */}
              {selectedProviderDraft.schema_locked ? (
                <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
                  This shared provider&apos;s schema is locked. Manage the lock on the provider.
                </div>
              ) : null}
              <button className="btn btnSm" type="button" onClick={() => void onDetachSharedProvider()}>
                Convert to local copy
              </button>
            </>
          ) : null}
        </>
      ) : null}
      <div className="field">
        <label className="label">Description</label>
        <textarea
          className="textarea"
          rows={3}
          placeholder="Optional description for this node…"
          value={selectedNode.data.description ?? ""}
          onChange={(e) => setNodeData(selectedNode.id, (data) => ({ ...data, description: e.target.value }))}
        />
      </div>
      <div className="field">
        <label className="label">Execution mode</label>
        <select
          className="input"
          value={selectedNode.data.config.execution_mode === "isolated" ? "isolated" : "concurrent"}
          onChange={(e) =>
            setNodeData(selectedNode.id, (data) => ({
              ...data,
              config: { ...data.config, execution_mode: e.target.value },
            }))
          }
        >
          <option value="concurrent">Concurrent</option>
          <option value="isolated">Isolated</option>
        </select>
        <div className="help">
          Concurrent providers may run at the same time as other SQL providers in this
          operation. Isolated runs this provider on its own — no other SQL provider in the
          operation executes against its source at the same time.
        </div>
      </div>
      {selectedProviderDraft && selectedProviderRef?.type === "local" ? (
        <>
          <div className="field">
            <label className="label">Connector</label>
            <select
              className="input"
              value={selectedProviderDraft.connector_id}
              onChange={(e) =>
                void updateMaterialProviderDraft(selectedNode.id, (draft) => ({ ...draft, connector_id: e.target.value }))
              }
            >
              <option value="">Select connector</option>
              {connectors.map((connector) => (
                <option key={connector.id} value={connector.id}>
                  {connector.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label
              className="label"
              htmlFor="op-local-sql-lock"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}
            >
              <input
                id="op-local-sql-lock"
                type="checkbox"
                checked={Boolean(selectedProviderDraft.source_sql_locked)}
                disabled={
                  !Boolean(selectedProviderDraft.source_sql_locked) && !selectedProviderDraft.source_spec.sql.trim()
                }
                onChange={(e) =>
                  void updateMaterialProviderDraft(selectedNode.id, (draft) => ({
                    ...draft,
                    source_sql_locked: e.target.checked
                  }))
                }
              />
              Production SQL lock
            </label>
          </div>
          <button className="btn btnSm" type="button" onClick={() => void onPromoteLocalProvider()}>
            Promote to shared library
          </button>
        </>
      ) : null}
      <NodeMetadataFields meta={operationMeta} />
    </>
  );

  const scopeSection =
    isProviderNode && providerInspectorTab === "scope" ? (
      <div className="reportSection" style={{ display: "grid", gap: 10 }}>
        {(() => {
          const scopeEdge = edges.find((e) => e.target === selectedNode.id && e.data?.edge_type === "scope");
          if (!scopeEdge) {
            return (
              <>
                <div className="muted" style={{ fontWeight: 600 }}>
                  Scope: None
                </div>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                  Connect another SQL Provider&apos;s tabular output to this node&apos;s scope port (left, orange) to enable per-row
                  execution. Use scope column names as variables in query bindings.
                </p>
              </>
            );
          }
          const src = nodes.find((n) => n.id === scopeEdge.source);
          const schema = nodeOutputs[scopeEdge.source]?.outputSchema;
          const columnNames = schema?.map((c) => c.name) ?? [];
          const scopeCfg = (selectedNode.data.config as { scope?: { mode?: string; variable?: string; column?: string } })
            .scope;
          // Scope has ONE mode now. `fanout` was offered here and selectable
          // while the engine treated fan-out scope edges as inert — a control
          // that did nothing. Per-row work moved to the loop, which repeats a
          // whole series of steps per item rather than parameterising one
          // query. A config with no mode reads as "not configured yet", which
          // is what it always meant.
          const scopeConfigured = scopeCfg?.mode === "list";
          const scopeVariable = scopeCfg?.variable ?? "";
          const scopeColumn = scopeCfg?.column ?? "";
          const variableValid = scopeVariable === "" || /^[A-Za-z_][A-Za-z0-9_]*$/.test(scopeVariable);
          const variableCollides = scopeVariable !== "" && operationVariableNames.includes(scopeVariable);
          const updateScope = (patch: Record<string, unknown>) =>
            setNodeData(selectedNode.id, (data) => {
              const prev = (data.config.scope as Record<string, unknown> | undefined) ?? {};
              return { ...data, config: { ...data.config, scope: { ...prev, ...patch } } };
            });
          return (
            <>
              <div className="muted" style={{ fontWeight: 600 }}>
                Scope: Connected
              </div>
              <div style={{ fontSize: 13 }}>
                Source:{" "}
                <strong>{src?.data.label?.trim() || (src ? NODE_LABELS[src.data.nodeType] : "Unknown")}</strong>
              </div>

              {!scopeConfigured ? (
                <>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    Reduce a column from the connected step to a distinct list of values, and reference that list in
                    this query — so it runs once, constrained to those values.
                  </p>
                  <div className="actions">
                    <button
                      className="btn btnSm"
                      type="button"
                      onClick={() => updateScope({ mode: "list", column: scopeColumn || columnNames[0] || "" })}
                    >
                      Use a value list
                    </button>
                  </div>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    To run this step once per row instead, put it inside a loop.
                  </p>
                </>
              ) : (
                <>
                  <label className="field">
                    <span className="label">Scope variable</span>
                    <input
                      className="input"
                      value={scopeVariable}
                      placeholder="e.g. parts"
                      onChange={(e) => updateScope({ variable: e.target.value.trim() })}
                    />
                  </label>
                  <div className="help">
                    {!variableValid ? (
                      "Use letters, digits, and underscore; cannot start with a digit."
                    ) : variableCollides ? (
                      "This name matches an operation variable — choose a different name."
                    ) : (
                      <>
                        Reference it in your SQL as <span className="mono">:{scopeVariable || "parts"}</span>, e.g.{" "}
                        <span className="mono">WHERE part_id IN (:{scopeVariable || "parts"})</span>.
                      </>
                    )}
                  </div>

                  <label className="field">
                    <span className="label">Source column</span>
                    <select
                      className="select"
                      value={scopeColumn}
                      disabled={columnNames.length === 0}
                      onChange={(e) => updateScope({ column: e.target.value })}
                    >
                      {columnNames.length === 0 ? (
                        <option value="">Run the scope source to load columns</option>
                      ) : (
                        <>
                          {!scopeColumn ? <option value="">Select a column…</option> : null}
                          {columnNames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </label>
                  {columnNames.length === 0 ? (
                    <div className="help">The scope source has not produced a schema yet — run it to pick a column.</div>
                  ) : null}
                </>
              )}

              <div className="muted" style={{ fontSize: 12 }}>
                Available columns
              </div>
              {!schema || schema.length === 0 ? (
                <div className="muted">Not available</div>
              ) : (
                <div className="tableWrap">
                  <table className="table tableCompact">
                    <thead>
                      <tr>
                        <th>Column</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schema.map((col) => (
                        <tr key={col.name}>
                          <td>{col.name}</td>
                          <td className="muted">{col.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()}
      </div>
    ) : null;

  const sqlSection =
    selectedProviderDraft && providerInspectorTab === "sql" ? (
      <>
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">
            {selectedProviderRef?.type === "shared"
              ? "SQL (read-only)"
              : selectedProviderDraft.source_sql_locked
              ? "SQL (locked for production)"
              : "SQL"}
          </h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn btnSm"
              type="button"
              onClick={() => void copyProviderSql()}
              disabled={!selectedProviderDraft.source_spec.sql.trim()}
            >
              Copy SQL
            </button>
            <button
              className="btn btnSm"
              type="button"
              onClick={() => void executeSelectedProviderSql()}
              disabled={nodeRunActive || selectedProviderPreview?.loading === true}
            >
              {nodeRunActive || selectedProviderPreview?.loading === true
                ? "Executing..."
                : "Execute"}
            </button>
            {nodeRunActive ? (
              <button className="btn btnSm" type="button" onClick={() => void onStopExecution()}>
                Stop
              </button>
            ) : null}
          </div>
        </div>
        {nodeRunActive || selectedProviderPreview?.loading === true ? (
          <div className="help">Running…</div>
        ) : null}
        <div className="queryEditorCm">
          <CodeMirror
            value={selectedProviderDraft.source_spec.sql}
            height="240px"
            extensions={sqlExtensions}
            placeholder="SELECT ..."
            readOnly={sqlEditorLocked}
            editable={!sqlEditorLocked}
            onChange={(val) =>
              void updateMaterialProviderDraft(selectedNode.id, (draft) => ({
                ...draft,
                source_spec: { specVersion: "source.v1", kind: "sql", sql: val }
              }))
            }
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              highlightActiveLine: true,
              bracketMatching: true,
              autocompletion: false
            }}
          />
        </div>
        <div className="help">
          Parameterize with <code className="mono">:name</code> (single colon) — e.g.{" "}
          <code className="mono">{"WHERE order_date > DATEADD(year, :yr_depth, GETDATE())"}</code>. Define{" "}
          <code className="mono">name</code> as an operation Variable (a constant you can edit anytime) or a
          provider Parameter. Numbers stay unquoted; text and dates are quoted automatically. Don&apos;t use{" "}
          <code className="mono">{"{{name}}"}</code>, <code className="mono">{"${name}"}</code>, or{" "}
          <code className="mono">$name</code>.
        </div>
        {selectedProviderPreview?.error ? (
          <div className="muted" style={{ color: "var(--danger-700)" }}>
            {selectedProviderPreview.error}
          </div>
        ) : null}
        {providerPreviewTanModel ? (
          <div className="card" style={{ marginTop: 8, overflow: "hidden", minWidth: 0 }}>
            {previewHeaderLabel(selectedProviderPreview?.result) ? (
              <div className="muted" style={{ padding: "6px 8px" }}>{previewHeaderLabel(selectedProviderPreview?.result)}</div>
            ) : null}
            <TanTableViz model={providerPreviewTanModel} />
          </div>
        ) : null}
      </>
    ) : null;

  const parametersSection =
    selectedProviderDraft && providerInspectorTab === "parameters" ? (
      <>
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">Parameters{selectedProviderRef?.type === "shared" ? " (read-only)" : ""}</h3>
          {selectedProviderRef?.type !== "shared" ? (
            <button className="btn btnSm" type="button" onClick={openNewParameterModal}>
              Add parameter
            </button>
          ) : null}
        </div>
        {selectedProviderDraft.parameters.length === 0 ? <div className="muted">No parameters</div> : null}
        {selectedProviderDraft.parameters.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                {selectedProviderRef?.type !== "shared" ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {selectedProviderDraft.parameters.map((param, idx) => (
                <tr key={`param-row-${idx}`}>
                  <td>{param.name}</td>
                  <td>{param.type}</td>
                  <td>{param.required ? "Yes" : "No"}</td>
                  {selectedProviderRef?.type !== "shared" ? (
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <button className="linkBtn menuLinkAction" type="button" onClick={() => openEditParameterModal(idx)}>
                          Edit
                        </button>
                        <button
                          className="linkBtn menuLinkAction linkBtnDanger"
                          type="button"
                          onClick={() => {
                            void deleteParameterFromList(idx);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </>
    ) : null;

  const bindingsSection =
    selectedProviderDraft && providerInspectorTab === "bindings" ? (
      <div className="reportSection">
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">Bindings</h3>
          {selectedProviderDraft.parameters.length > 0 ? (
            <button className="linkBtn menuLinkAction" type="button" onClick={openAddBindingModal}>
              Add binding
            </button>
          ) : null}
        </div>
        {selectedProviderDraft.parameters.length === 0 ? <div className="muted">No query parameters to bind.</div> : null}
        {selectedProviderDraft.parameters.length > 0 ? (
          (() => {
            const bindings = (selectedNode.data.config.bindings as Record<string, BindingSource> | undefined) ?? {};
            const entries = Object.entries(bindings).filter(([, current]) => current !== undefined);
            if (entries.length === 0) return <div className="muted">No bindings yet.</div>;
            return (
              <div style={{ display: "grid", gap: 8 }}>
                {entries.map(([paramName, current]) => {
                  const param = selectedProviderDraft!.parameters.find((entry) => entry.name === paramName);
                  if (!param) return null;
                  return (
                    <div
                      key={`binding-item-${paramName}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        border: "1px solid var(--line)",
                        borderRadius: 8,
                        padding: "8px 10px"
                      }}
                    >
                      <div style={{ fontSize: 13, lineHeight: "18px" }}>{bindingDescription(paramName, current)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <button className="linkBtn menuLinkAction" type="button" onClick={() => openBindingModal(param)}>
                          Edit
                        </button>
                        <button
                          className="linkBtn menuLinkAction linkBtnDanger"
                          type="button"
                          onClick={() => void clearBinding(paramName)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : null}
      </div>
    ) : null;

  // ADR-0147: a node bound to a SHARED provider defers to the provider's
  // override map — the Schema tab links to the provider's own Schema Contract
  // instead of editing inline (one path per action).
  const boundSharedProviderId =
    selectedProviderRef?.type === "shared" ? selectedProviderRef.provider_id : null;
  const canEditDomains = Boolean(onColumnDomainChange) && !boundSharedProviderId;

  const schemaSection =
    selectedProviderDraft && providerInspectorTab === "schema" ? (
      <div className="reportSection">
        <h3 className="reportSectionTitle">Schema Contract</h3>
        {selectedProviderDraft.schema_contract?.columns && selectedProviderDraft.schema_contract.columns.length > 0 ? (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Column Name</th>
                  <th>Type</th>
                  <th>Domain</th>
                </tr>
              </thead>
              <tbody>
                {selectedProviderDraft.schema_contract.columns.map((column) => (
                  <tr key={column.name}>
                    <td>{column.name}</td>
                    <td>{column.rawType ?? column.type}</td>
                    <td>
                      {canEditDomains ? (
                        <ColumnDomainSelect
                          columnName={column.name}
                          resolvedDomain={column.type}
                          override={columnOverrides?.[column.name]}
                          disabled={retypeBusy}
                          onChange={onColumnDomainChange!}
                        />
                      ) : (
                        column.type
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {boundSharedProviderId ? (
              <div className="help" style={{ marginTop: 6 }}>
                This node uses the shared provider «{selectedProviderDraft.name}» — set column
                types on its{" "}
                <Link to={`/app/build/providers/${encodeURIComponent(boundSharedProviderId)}`}>
                  Schema Contract
                </Link>
                .
              </div>
            ) : null}
          </>
        ) : (
          <div className="muted">No results yet — run the provider SQL to populate schema.</div>
        )}
      </div>
    ) : null;

  const cacheSection =
    selectedProviderDraft && providerInspectorTab === "cache" ? (
      <div className="reportSection" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">
            Cached data
            {nodeOutputs[selectedNode.id]?.rowCount != null
              ? ` — ${nodeOutputs[selectedNode.id].rowCount!.toLocaleString()} rows`
              : ""}
          </h3>
          <div className="toolbarGroup">
            <button
              className="btn btnSm"
              type="button"
              disabled={nodeCacheRefreshBusy || operationStatus === "validate"}
              onClick={() => void refreshProviderCache(selectedNode.id)}
              title={
                operationStatus === "validate" ? "Operation requires validation" : "Re-run the provider SQL and update the cache"
              }
            >
              {nodeCacheRefreshBusy ? "Refreshing…" : "Refresh cache"}
            </button>
          </div>
        </div>

        <NodeOutputStats rec={nodeOutputs[selectedNode.id]} />

        {nodeCacheLoading[selectedNode.id] || !Object.prototype.hasOwnProperty.call(nodeCache, selectedNode.id) ? (
          <div className="muted">Loading cache…</div>
        ) : nodeCacheTanModel === null ? (
          <div className="muted">No cached data. Click &quot;Refresh cache&quot; to run the provider SQL and populate the cache.</div>
        ) : (
          <>
            {previewHeaderLabel(nodeCache[selectedNode.id]) ? (
              <div className="muted" style={{ paddingBottom: 6 }}>{previewHeaderLabel(nodeCache[selectedNode.id])}</div>
            ) : null}
            <TanTableViz model={nodeCacheTanModel} />
          </>
        )}
      </div>
    ) : null;

  const historyContent = (
    <div className="reportSection" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <NodeRunHistory
        runs={nodeRunHistory[selectedNode.id] ?? []}
        loading={nodeRunHistoryLoading[selectedNode.id] ?? false}
        onSelectRun={onSelectHistoryRun}
        onOpenTrace={onOpenTrace}
      />
    </div>
  );

  // Setup sub-sections that require a loaded draft (Scope works without one).
  const setupNeedsDraft =
    providerInspectorTab === "sql" || providerInspectorTab === "parameters" || providerInspectorTab === "bindings";

  return (
    <InspectorTabs
      surfaceId="operationEditor"
      tabGroupId="provider"
      activeSlot={PROVIDER_SLOT_OF[providerInspectorTab]}
      onSlotChange={(slot) => setProviderInspectorTab(providerDefaultTabForSlot(slot))}
      tabs={[
        {
          slot: "setup",
          content: (
            <>
              {renderSubTabBar("Provider setup sections", setupSubTabs)}
              {scopeSection}
              {sqlSection}
              {parametersSection}
              {bindingsSection}
              {!selectedProviderDraft && setupNeedsDraft ? draftMissingFallback : null}
            </>
          )
        },
        {
          slot: "data",
          content: (
            <>
              {renderSubTabBar("Provider data sections", dataSubTabs)}
              {schemaSection}
              {cacheSection}
              {!selectedProviderDraft ? draftMissingFallback : null}
            </>
          )
        },
        {
          slot: "runs",
          content: historyContent
        },
        {
          slot: "info",
          content: infoContent
        }
      ]}
    />
  );
}
